import { Link, useNavigate } from "react-router-dom";
import AssignmentTable from "../../sections/student/AssignmentTable";
import { brandingConfig } from "../../../config/branding";
import { useEffect, useState } from "react";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";
import StudentSettingsModal from "../../modals/StudentSettingsModal";
import { Settings } from "lucide-react";
import { useUiLang } from "../../../stores/uiLang";
import { studentLogoutPath } from "../../../utils/kidLogin";

// Run E2 J2: K-5 student dashboard. The PFL parts are gone: "State" (Oklahoma
// fallback), "My Progress" (PFL state standards, opened PFL Day 1 / Day 2
// pages), "My Learning Path" and the hidden high-school portfolio (the files
// are kept). "My Progress" now counts the student's Project Explore chapter
// assignments. Text is in English and Spanish (shared UI language).
const DASH_T = {
  en: {
    title: "My Learning Dashboard",
    subtitle: "Find your chapters, keep reading, and see what you finished.",
    settings: "Settings",
    done: "I'm done",
    doneHelp: "Sign out so the next student can sign in",
    myInfo: "My Information",
    classLabel: "Class",
    school: "School",
    district: "District",
    noClass: "Not part of any class",
    noSchool: "Not part of any school",
    noDistrict: "Not part of any district",
    progress: "My Progress",
    loading: "Loading...",
    noChapters: "No chapters assigned yet.",
    finished: (done: number, total: number) =>
      `${done} of ${total} ${total === 1 ? "chapter" : "chapters"} finished`,
    inProgress: (n: number) => `In progress: ${n}`,
    notStarted: (n: number) => `Not started: ${n}`,
    waiting: (n: number) => `Waiting for teacher: ${n}`,
    help: "Help Center",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    accessibility: "Accessibility",
    rights: "All rights reserved.",
  },
  es: {
    title: "Mi panel de aprendizaje",
    subtitle: "Encuentra tus capítulos, sigue leyendo y mira lo que terminaste.",
    settings: "Configuración",
    done: "Ya terminé",
    doneHelp: "Cierra tu sesión para que entre otro estudiante",
    myInfo: "Mi información",
    classLabel: "Clase",
    school: "Escuela",
    district: "Distrito",
    noClass: "Sin clase asignada",
    noSchool: "Sin escuela asignada",
    noDistrict: "Sin distrito asignado",
    progress: "Mi progreso",
    loading: "Cargando...",
    noChapters: "Todavía no tienes capítulos asignados.",
    finished: (done: number, total: number) =>
      `${done} de ${total} ${total === 1 ? "capítulo terminado" : "capítulos terminados"}`,
    inProgress: (n: number) => `En curso: ${n}`,
    notStarted: (n: number) => `Sin empezar: ${n}`,
    waiting: (n: number) => `Esperando al maestro: ${n}`,
    help: "Centro de ayuda",
    privacy: "Política de privacidad",
    terms: "Términos del servicio",
    accessibility: "Accesibilidad",
    rights: "Todos los derechos reservados.",
  },
};

type PeProgress = {
  total: number;
  done: number;
  inProgress: number;
  notStarted: number;
  waiting: number;
};

const ICON_CLASS =
  "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4";
const ICON_SCHOOL =
  "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253";
const ICON_PLACE =
  "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z";

const StudentDashboard = () => {
  const user = useUserStore((state) => state.user);
  const profile = useUserStore((state) => state.profile);
  const setUser = useUserStore((state) => state.setUser);
  const setProfile = useUserStore((state) => state.setProfile);
  const navigate = useNavigate();
  const lang = useUiLang();
  const t = DASH_T[lang];
  const [peProgress, setPeProgress] = useState<PeProgress | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [studentDistrict, setStudentDistrict] = useState<any>(null);
  const [studentSchool, setStudentSchool] = useState<any>(null);
  const [studentClass, setStudentClass] = useState<any>(null);
  useEffect(() => {
    if (!profile) return;
    async function loadData() {
      await Promise.all([
        loadPeProgress(),
        loadStudentDistrict(),
        loadStudentSchool(),
        loadStudentClass(),
      ]);
    }
    loadData();
  }, [profile]);

  // Run E2 J1: student-teacher chat removed (K-5). The "Message Teacher"
  // handler that created a chat room and opened /chat/:id is gone.

  // Progress from the student's own PE chapter assignments (type "pe_chapter").
  const loadPeProgress = async () => {
    if (!user?.id) return;
    try {
      setLoadingProgress(true);
      const { data, error } = await supabase
        .from("student_assignments")
        .select("status, is_grade_released, assignments!inner(type)")
        .eq("student_id", user.id)
        .eq("assignments.type", "pe_chapter");
      if (error) throw error;
      const rows = (data || []) as { status: string; is_grade_released?: boolean | null }[];
      setPeProgress({
        total: rows.length,
        // CT-1: a submitted row whose grade is released counts as finished.
        done: rows.filter(
          (r) =>
            r.status === "completed" ||
            (r.status === "submitted" && r.is_grade_released)
        ).length,
        waiting: rows.filter(
          (r) => r.status === "submitted" && !r.is_grade_released
        ).length,
        inProgress: rows.filter((r) => r.status === "in_progress").length,
        notStarted: rows.filter(
          (r) => r.status === "not_started" || r.status === "overdue"
        ).length,
      });
    } catch (error) {
      console.error("Error loading PE progress:", error);
      setPeProgress(null);
    } finally {
      setLoadingProgress(false);
    }
  };
  async function loadStudentDistrict() {
    if (!profile?.district) return;
    const { data, error } = await supabase
      .from("districts")
      .select("*")
      .eq("id", profile.district)
      .maybeSingle();
    if (error) {
      console.error(error);
      setStudentDistrict(null);
    }
    setStudentDistrict(data);
  }
  async function loadStudentSchool() {
    if (!profile?.school) return;
    const { data, error } = await supabase
      .from("schools")
      .select("*")
      .eq("id", profile.school)
      .maybeSingle();
    if (error) {
      console.error(error);
      setStudentSchool(null);
    }
    setStudentSchool(data);
  }
  async function loadStudentClass() {
    if (!profile?.class) return;
    const { data, error } = await supabase
      .from("classes")
      .select("*")
      .eq("id", profile.class)
      .maybeSingle();

    if (error) {
      console.error(error);
      setStudentClass(null);
    }
    setStudentClass(data);
  }

  // Shared classroom devices: one big button signs this student out and goes back to the login the student
  // uses (picture-PIN kids: the kid login with their class code filled in; others: the login portal).
  const [signingOut, setSigningOut] = useState(false);
  async function imDone() {
    setSigningOut(true);
    const next = studentLogoutPath(user?.email, studentClass?.join_code);
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) console.error("Error signing out:", error.message);
    setUser(undefined);
    setProfile(null);
    navigate(next);
  }

  const pct =
    peProgress && peProgress.total > 0
      ? Math.round((peProgress.done / peProgress.total) * 100)
      : 0;

  const infoItems: { label: string; value: string; icon: string }[] = [
    {
      label: t.classLabel,
      value: studentClass ? studentClass.name : t.noClass,
      icon: ICON_CLASS,
    },
    {
      label: t.school,
      value: studentSchool ? studentSchool.name : t.noSchool,
      icon: ICON_SCHOOL,
    },
    {
      label: t.district,
      value: studentDistrict ? studentDistrict.name : t.noDistrict,
      icon: ICON_PLACE,
    },
  ];

  return (
    <>
      <style>{`
        body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
            background-color: #f9fafb;
        }
      `}</style>
      <div className="bg-white py-8 pt-24 shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl sm:truncate">
                {t.title}
              </h1>
              <p className="mt-2 text-gray-600">{t.subtitle}</p>
            </div>
            <div className="mt-4 md:mt-0 md:ml-4 flex flex-wrap items-center gap-3">
              {/* Run E2 J1: student-teacher chat removed (K-5) */}
              <button
                type="button"
                onClick={imDone}
                disabled={signingOut}
                title={t.doneHelp}
                aria-label={`${t.done}. ${t.doneHelp}`}
                className="rounded-2xl bg-green-600 px-6 py-3 text-xl font-bold text-white shadow hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-60"
              >
                👋 {t.done}
              </button>
              <button
                onClick={() => setIsSettingsModalOpen(true)}
                className="btn btn-small btn-secondary"
              >
                <Settings className="-ml-1 mr-2 h-5 w-5 text-[var(--color-btnSecondaryText)]" />
                {t.settings}
              </button>
            </div>
          </div>
        </div>
      </div>
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Student Information Section */}
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t.myInfo}</h2>
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {infoItems.map((item) => (
              <div key={item.label} className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={item.icon}
                    />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-500">
                    {item.label}
                  </p>
                  <p className="text-sm text-gray-900 break-words">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* My Progress: Project Explore chapter assignments */}
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t.progress}</h2>
        <div className="bg-white shadow rounded-lg p-6 mb-10">
          {loadingProgress ? (
            <p className="text-sm text-gray-500">{t.loading}</p>
          ) : !peProgress || peProgress.total === 0 ? (
            <p className="text-sm text-gray-600">{t.noChapters}</p>
          ) : (
            <div>
              <p className="text-base font-semibold text-gray-900">
                {t.finished(peProgress.done, peProgress.total)}
              </p>
              <div
                className="mt-3 h-3 w-full rounded-full bg-gray-200 overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                aria-label={t.progress}
              >
                <div
                  className="h-full rounded-full bg-indigo-600"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                <span>{t.inProgress(peProgress.inProgress)}</span>
                <span>{t.notStarted(peProgress.notStarted)}</span>
                {peProgress.waiting > 0 && (
                  <span>{t.waiting(peProgress.waiting)}</span>
                )}
              </div>
            </div>
          )}
        </div>

        <AssignmentTable />
      </main>
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex flex-wrap justify-center md:order-2 gap-x-6 gap-y-2">
              <Link to="/support" className="text-gray-500 hover:text-gray-600">
                <span className="text-sm">{t.help}</span>
              </Link>
              <Link to="/privacy" className="text-gray-500 hover:text-gray-600">
                <span className="text-sm">{t.privacy}</span>
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-600">
                <span className="text-sm">{t.terms}</span>
              </Link>
              <Link
                to="/accessibility"
                className="text-gray-500 hover:text-gray-600"
              >
                <span className="text-sm">{t.accessibility}</span>
              </Link>
            </div>
            <div className="mt-8 md:mt-0 md:order-1">
              <p className="text-center text-sm text-gray-500">
                &copy; {new Date().getFullYear()} {brandingConfig.siteTitle}.{" "}
                {t.rights}
              </p>
            </div>
          </div>
        </div>
      </footer>

      <StudentSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </>
  );
};

export default StudentDashboard;
